import { CompositeLayer, Position, UpdateParameters } from '@deck.gl/core/typed';
import { IconLayer, IconLayerProps } from '@deck.gl/layers/typed';
import { KineticMarkerData } from 'components/maps/MapMarkerContext';
import { isNonNull } from 'kineticUtils';
import Supercluster from 'supercluster';
interface PickingIconLayerProps extends IconLayerProps {
  cluster: boolean;
  sizeScale: number;
  getPosition: (iconData: any) => Position;
  z?: number;
  order?: number;
}

interface PickingIconLayerState {
  data?: any[];
  index?: Supercluster;
  z?: number;
}

interface ClusterProperties {
  cluster?: boolean;
  clusterType?: string;
  clusterSvg?: string;
  id?: string;
}

interface ClusterItem {
  properties: ClusterProperties;
  clusters?: { [type: string]: { svg: string; count: number; ids: string[] } };
  onClickZoomLevel?: number;
  id?: number;
  geometry: {
    type: string;
    coordinates: Position;
  };
  type: string;
}

interface ChildItem {
  properties: {
    clusterType?: string;
    clusterSvg?: string;
    id?: string;
  };
}

export default class PickingIconLayer extends CompositeLayer<PickingIconLayerProps> {
  shouldUpdateState({ changeFlags }: { changeFlags: any }) {
    return changeFlags.somethingChanged;
  }

  getClusterGroups = (children: ChildItem[]) => {
    const groups: { [type: string]: { svg: string; count: number; ids: string[] } } = {};
    const groupIds: Record<string, any> = {};
    children.forEach((child) => {
      const type = child.properties.clusterType!;

      const id: string = child.properties.id!;
      groupIds[type] = groupIds[type] ? [...groupIds[type], id] : [id];
      groups[type] = {
        svg: child.properties.clusterSvg!,
        count: groups[type]?.count ? groups[type].count + 1 : 1,
        ids: groupIds[type],
      };
    });

    return groups;
  };

  transformCluster = (
    propsData?: Map<string, KineticMarkerData>,
    clusterIndex?: Supercluster<ClusterProperties>
  ) => {
    const z = Math.floor(this.context.viewport.zoom);
    const cluster: ClusterItem[] = [
      ...(clusterIndex?.getClusters([-180, -90, 180, 90], z) ?? []),
    ] as ClusterItem[];

    cluster.forEach((d, index) => {
      if (d.properties.cluster) {
        const groups = this.getClusterGroups(clusterIndex?.getLeaves(d.id!, Infinity) ?? []);
        const zoomLevel = clusterIndex?.getClusterExpansionZoom(d.id!);
        cluster[index].clusters = groups;
        cluster[index].onClickZoomLevel = zoomLevel;
      }

      if (!d.properties.cluster && propsData) {
        const properties = propsData?.get(d.properties.id!)!;
        const geometry = {
          type: 'Point',
          coordinates: properties.coordinates,
        };
        cluster[index] = {
          ...cluster[index],
          properties,
          geometry,
        };
      }
    });

    return cluster;
  };

  transformProp = (data: KineticMarkerData) => {
    let position = data.coordinates as [number, number];
    if (!position || position.length !== 2) {
      return null;
    }
    return {
      geometry: {
        type: 'Point' as 'Point',
        coordinates: position,
      },
      properties: data,
      type: 'Feature' as 'Feature',
    };
  };

  transformPropsData = (data: KineticMarkerData[]) => {
    return data.map((d) => this.transformProp(d)).filter((obj) => !!obj);
  };

  updateState({ props, oldProps, changeFlags }: UpdateParameters<this>) {
    const propsData = this.props.data as Map<string, KineticMarkerData>;
    if (props.cluster) {
      const z = Math.floor(this.context.viewport.zoom);
      const rebuildIndex = changeFlags.dataChanged || props.sizeScale !== oldProps.sizeScale;
      const newState: PickingIconLayerState = {};
      if (rebuildIndex) {
        const index = new Supercluster({
          maxZoom: 7,
          radius: 50 * Math.sqrt(2),
        });
        const arrayData = [...propsData.values()];
        index.load(this.transformPropsData(arrayData).filter(isNonNull));
        newState.index = index;
      }
      if (rebuildIndex || z !== this.state.z) {
        const cluster = this.transformCluster(propsData, newState.index ?? this.state.index);
        newState.data = cluster;
        newState.z = z;
        this.setState(newState);
      } else {
        this.updateData(!!changeFlags.dataChanged);
      }
    } else {
      this.updateData(!!changeFlags.dataChanged);
    }
  }

  updateData(_dataChanged: boolean) {
    const z = Math.floor(this.context.viewport.zoom);
    const propsData = this.props.data as Map<string, KineticMarkerData>;

    // TODO avoid updating the data reference if the props data reference hasn't changed
    // Icons
    // data hasn't changed don't change reference but update underlying properties
    // if (!dataChanged) {
    //   for (let i = 0; i < this.state.data.length; i++) {
    //     if (!this.state.data[i].properties.cluster) {
    //       this.state.data[i] = this.transformProp(propsData.get(this.state.data[i].properties.id));
    //     }
    //   }
    //   return;
    // }
    if (!this.props.cluster) {
      const arrayData = [...(propsData as Map<string, KineticMarkerData>).values()];
      const transformedData = this.transformPropsData(arrayData);
      this.setState({
        data: transformedData,
      });
    } else {
      const cluster = this.transformCluster(propsData, this.state.index);
      this.setState({
        data: cluster,
        z,
      });
    }
  }

  renderLayers() {
    const { data } = this.state;
    return new IconLayer(
      this.getSubLayerProps({
        ...this.props,
        data,
      })
    );
  }
}
